import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface PricingTier {
  id: string;
  tier_name: string;
  max_jobs_per_month: number | null;
}

interface CreateOrgDialogProps {
  onSuccess: () => void;
}

export function CreateOrgDialog({ onSuccess }: CreateOrgDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    adminEmail: "",
    pricingTierId: ""
  });

  useEffect(() => {
    if (open) {
      loadPricingTiers();
    }
  }, [open]);

  const loadPricingTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .order('tier_name');

      if (error) throw error;
      setPricingTiers(data || []);
    } catch (error: any) {
      console.error('Failed to load pricing tiers:', error);
      toast.error("Failed to load pricing tiers");
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Organization name is required");
      return;
    }

    if (!formData.adminEmail.trim()) {
      toast.error("Admin email is required");
      return;
    }

    if (!formData.pricingTierId) {
      toast.error("Please select a pricing tier");
      return;
    }

    try {
      setLoading(true);

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.name,
          pricing_tier_id: formData.pricingTierId,
          subscription_status: 'trial'
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Invite admin user via edge function
      const { data: inviteData, error: inviteError } = await supabase.functions.invoke(
        'platform-admin-invite-user',
        {
          body: {
            email: formData.adminEmail,
            org_id: org.id,
            full_name: formData.name + ' Admin'
          }
        }
      );

      if (inviteError || inviteData?.error) {
        console.error('Invite error:', inviteError || inviteData.error);
        toast.warning(`Organization created, but failed to send invite: ${inviteError?.message || inviteData.error}`);
      } else {
        toast.success(`Organization created! Invitation sent to ${formData.adminEmail}`);
        
        // Send welcome email
        try {
          const { data: emailData, error: emailError } = await supabase.functions.invoke('send-welcome-email', {
            body: {
              email: formData.adminEmail,
              orgName: formData.name
            }
          });
          
          if (emailError) {
            console.error('Welcome email error:', emailError);
            toast.error(`Failed to send welcome email: ${emailError.message}`, {
              description: 'The organization was created successfully, but the welcome email could not be sent. Please check your email configuration.'
            });
          } else if (emailData?.error) {
            console.error('Welcome email API error:', emailData.error);
            toast.error('Failed to send welcome email', {
              description: emailData.details || emailData.error
            });
          } else {
            console.log('✅ Welcome email sent successfully:', emailData?.emailId);
          }
        } catch (emailError: any) {
          console.error('Failed to send welcome email:', emailError);
          toast.error('Welcome email failed to send', {
            description: 'Check that RESEND_API_KEY is configured in backend secrets'
          });
        }
      }

      setOpen(false);
      setFormData({ name: "", adminEmail: "", pricingTierId: "" });
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      toast.error(`Failed to create organization: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Building2 className="w-4 h-4" />
          Create New Organization
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Organization</DialogTitle>
          <DialogDescription>
            Set up a new contractor organization and invite the first admin user
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="org-name">Organization Name *</Label>
            <Input
              id="org-name"
              placeholder="e.g., ABC Roofing Co."
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pricing-tier">Pricing Tier *</Label>
            <Select 
              value={formData.pricingTierId} 
              onValueChange={(value) => setFormData({ ...formData, pricingTierId: value })}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a tier" />
              </SelectTrigger>
              <SelectContent>
                {pricingTiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id}>
                    {tier.tier_name}
                    {tier.max_jobs_per_month && ` (${tier.max_jobs_per_month} jobs/month)`}
                    {!tier.max_jobs_per_month && ' (Unlimited)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-email">First Admin User Email *</Label>
            <Input
              id="admin-email"
              type="email"
              placeholder="admin@company.com"
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              An invitation email will be sent to this address
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Organization
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
